import { useState, useEffect } from 'react';
import { ChevronDownIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ProductFilters = ({ filters, onFilterChange, onClearAll }) => {
  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setPriceRange({
      min: filters.minPrice || '',
      max: filters.maxPrice || ''
    });
  }, [filters.minPrice, filters.maxPrice]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/products/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback categories
      setCategories([
        { _id: 't-shirts', name: 'T-Shirts', slug: 't-shirts' },
        { _id: 'mugs', name: 'Mugs', slug: 'mugs' },
        { _id: 'keychains', name: 'Keychains', slug: 'keychains' },
        { _id: 'phone-cases', name: 'Phone Cases', slug: 'phone-cases' },
        { _id: 'hoodies', name: 'Hoodies', slug: 'hoodies' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    onFilterChange({ [name]: value });
  };

  const handlePriceChange = (type, value) => {
    const newPriceRange = { ...priceRange, [type]: value };
    setPriceRange(newPriceRange);
    onFilterChange({
      minPrice: newPriceRange.min,
      maxPrice: newPriceRange.max
    });
  };

  const hasActiveFilters = () => {
    return filters.category || filters.minPrice || filters.maxPrice;
  };

  const clearAllFilters = () => {
    setPriceRange({ min: '', max: '' });
    onFilterChange({
      category: '',
      minPrice: '',
      maxPrice: ''
    });
    if (onClearAll) onClearAll();
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Mobile Filter Toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center">
            <FunnelIcon className="h-5 w-5 text-gray-600 mr-2" />
            <span className="font-medium text-gray-900">Filtres</span>
            {hasActiveFilters() && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                Actifs
              </span>
            )}
          </div>
          <ChevronDownIcon 
            className={`h-5 w-5 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>
      </div>

      {/* Filter Content - Responsive Grid Layout */}
      <div className={`${isOpen ? 'block' : 'hidden'} md:block p-4 md:p-6`}>
        {/* Clear All Filters */}
        {hasActiveFilters() && (
          <div className="flex justify-between items-center pb-4 mb-6 border-b border-gray-200">
            <span className="text-sm text-gray-600">Filtres actifs</span>
            <button
              onClick={clearAllFilters}
              className="flex items-center text-sm text-red-600 hover:text-red-700"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Tout effacer
            </button>
          </div>
        )}

        {/* Responsive Grid for Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Category Filter */}
          <div>
            <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie
            </label>
            <select
              id="category-select"
              name="category"
              value={filters.category || ''}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((category) => (
                <option key={category._id || category.id} value={category._id || category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gamme de prix
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                id="min-price-input"
                name="minPrice"
                type="number"
                value={filters.minPrice || ''}
                onChange={(e) => handleInputChange('minPrice', e.target.value)}
                placeholder="Min"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                id="max-price-input"
                name="maxPrice"
                type="number"
                value={filters.maxPrice || ''}
                onChange={(e) => handleInputChange('maxPrice', e.target.value)}
                placeholder="Max"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Prix en HTG
            </div>
          </div>

          {/* Quick Filters */}
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Filtres rapides
            </label>
            <div className="space-y-2">
              <button
                onClick={() => handleInputChange('featured', !filters.featured)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  filters.featured
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Produits vedettes
              </button>
              <button
                onClick={() => handleInputChange('newArrivals', !filters.newArrivals)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  filters.newArrivals
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Nouveautés
              </button>
              <button
                onClick={() => handleInputChange('onSale', !filters.onSale)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  filters.onSale
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                En promotion
              </button>
            </div>
          </div>

          {/* Stock Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Disponibilité
            </label>
            <div className="space-y-2">
              <label htmlFor="in-stock-checkbox" className="flex items-center">
                <input
                  id="in-stock-checkbox"
                  name="inStock"
                  type="checkbox"
                  checked={filters.inStock !== false}
                  onChange={(e) => handleInputChange('inStock', e.target.checked ? undefined : false)}
                  className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-700">
                  En stock
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFilters;